import React__default from "react";
import clsx from "clsx";
import { Badge } from "../../badge/badge.mjs";
import { CloseButton } from "../../close-button/close-button.mjs";
import { getBackgroundColorClass } from "../../colors/index.mjs";
import "debounce";
import "tabbable";
import { useResponsiveProps } from "../../helpers/helper/hooks/use-responsive-props.mjs";
import { Image } from "../../image/image.mjs";
import { ObjectFit } from "../../object-fit/object-fit.mjs";
import { useRouter } from "../../helpers/router/use-router.mjs";
import { Tag } from "../../tag/tag.mjs";
import { TagGroup } from "../../tag-group/tag-group.mjs";
const CardContext = React__default.createContext({
  padding: "medium",
  background: "neutral-bg-level-2"
});
const Card = React__default.forwardRef(function Card2(props, ref) {
  const { responsive, ...rest } = props;
  return responsive ? /* @__PURE__ */ React__default.createElement(CardResponsive, { ...rest, responsive, ref }) : /* @__PURE__ */ React__default.createElement(CardInner, { ...rest, ref });
});
const CardResponsive = React__default.forwardRef(function CardResponsive2(props, ref) {
  const { responsive, ...rest } = props;
  const responsiveProps = useResponsiveProps({
    responsive
  });
  return /* @__PURE__ */ React__default.createElement(CardInner, { ...rest, ...responsiveProps, ref });
});
const CardInner = React__default.forwardRef(function CardInner2(props, ref) {
  const [isCardSelected, setIsCardSelected] = React__default.useState(props.isSelected || false);
  const {
    badgeTop,
    children,
    className,
    background,
    borderRadius = "medium",
    hoverStyle = "border",
    elevation: propElevation,
    onClick: propOnClick,
    onMouseLeave,
    onMouseEnter,
    onTransitionEnd,
    external = false,
    target,
    type,
    url,
    isActive,
    isDisabled,
    fullOpacity,
    style,
    tag,
    selectable,
    isSelected,
    padding = "medium",
    backgroundImage,
    element = "div",
    theme,
    leftBorderColor,
    leftBorderColorHex,
    image,
    isClosable,
    onClickClose,
    borderStyle,
    fullWidth,
    ...rest
  } = props;
  const CardTag = url ? "a" : element;
  const elevation = props.isActive ? 5 : props.elevation;
  const { navigate } = useRouter({ onClick: propOnClick });
  const onClick = !url || url && external ? propOnClick : void 0;
  const backgroundColor = React__default.useMemo(() => {
    if (background) {
      return background;
    } else if (isActive) {
      return "brand-primary-bg-subtle";
    }
    return "neutral-bg-level-2";
  }, [background, isActive]);
  const handleClick = React__default.useCallback(
    (event) => {
      if (!external && url) {
        navigate(event);
      }
      if (onClick) {
        onClick(event);
      }
      if (selectable && isSelected === void 0) {
        setIsCardSelected(!isCardSelected);
      }
    },
    [external, onClick, url, navigate, selectable, isSelected, isCardSelected]
  );
  const imageBEM = (image2) => {
    const classArray = ["card__background-image"];
    if (image2.className) {
      classArray.push(image2.className);
    }
    return classArray.join(" ");
  };
  const renderTags = (tags) => {
    let content;
    if (Array.isArray(tags)) {
      content = /* @__PURE__ */ React__default.createElement(TagGroup, { tags, size: "small" });
    } else {
      const tagProps = {
        className: "card__tag",
        size: "small",
        ...tags
      };
      content = /* @__PURE__ */ React__default.createElement(Tag, { ...tagProps });
    }
    return /* @__PURE__ */ React__default.createElement("div", { className: "card__tags" }, content);
  };
  const renderContent = () => /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, isClosable && onClickClose && /* @__PURE__ */ React__default.createElement(CloseButton, { className: "card__close", onClick: onClickClose }), image && /* @__PURE__ */ React__default.createElement("div", { className: "card__figure" }, /* @__PURE__ */ React__default.createElement(Image, { ...image, className: "card__image" })), children);
  return /* @__PURE__ */ React__default.createElement(
    CardContext.Provider,
    {
      value: React__default.useMemo(
        () => ({
          padding,
          background: backgroundColor
        }),
        [padding, backgroundColor]
      )
    },
    /* @__PURE__ */ React__default.createElement(
      CardTag,
      {
        className: clsx(
          "card",
          typeof borderRadius === "string" ? "card--border-radius-".concat(borderRadius) : borderRadius && Object.keys(borderRadius).map((key) => "card--border-radius-".concat(key, "-").concat(borderRadius[key])),
          backgroundColor && backgroundColor !== "transparent" && getBackgroundColorClass(backgroundColor),
          {
            ["card--type-".concat(type)]: type,
            ["card--link card--link-".concat(hoverStyle)]: (url || onClick) && !selectable,
            ["card--disabled"]: isDisabled,
            ["card--full-opacity"]: fullOpacity,
            ["card--with-background-image"]: backgroundImage,
            ["card--elevation-".concat(elevation)]: elevation,
            ["card--badge"]: badgeTop,
            ["is-active"]: isActive,
            ["card--selectable"]: selectable,
            ["is-selected"]: isSelected != null ? isSelected : isCardSelected,
            ["card--left-bordered"]: leftBorderColor != null ? leftBorderColor : leftBorderColorHex,
            ["card--left-bordered-".concat(leftBorderColor)]: leftBorderColor,
            ["card--full-width"]: fullWidth,
            ["card--is-closable"]: isClosable,
            ["card--border-".concat(borderStyle)]: borderStyle
          },
          className
        ),
        href: url,
        onClick: handleClick,
        target,
        ref,
        onMouseEnter,
        onMouseLeave,
        onTransitionEnd,
        style: {
          borderLeftColor: leftBorderColorHex != null ? leftBorderColorHex : void 0
        },
        "data-theme": theme,
        ...rest
      },
      backgroundImage && /* @__PURE__ */ React__default.createElement(ObjectFit, { fit: "cover", positionX: "center", positionY: "center" }, /* @__PURE__ */ React__default.createElement(Image, { ...backgroundImage, className: imageBEM(backgroundImage), pictureClassName: "card__background-image-picture" })),
      tag && renderTags(tag),
      typeof badgeTop === "object" && /* @__PURE__ */ React__default.createElement("div", { className: "card__badge-wrapper" }, /* @__PURE__ */ React__default.createElement("div", { className: "card__badge-wrapper-inner" }, /* @__PURE__ */ React__default.createElement(Badge, { className: "card__badge", ...badgeTop }))),
      renderContent()
    )
  );
});
export {
  Card,
  CardContext,
  CardResponsive
};
